import { FC } from 'react'
import shallow from 'zustand/shallow'
import { useTranslation } from 'react-i18next'
import { Col, Divider, Checkbox } from 'antd'

import PerText from '../Text'
import PerSpacer from '../Spacer'
import PerFlexRow from '../FlexRow'
import { useAuth } from '../../../context/auth-context'
import { Country } from '../../../types'
import { useDateTime } from '../../../shared/useDateTime'
import { PerProgressBar } from '../ProgressBar'
import { TargetsModalComment } from './TargetsModalComment'
import { useTargetsModalStore } from './TargetsModalStore'
import { useAmountAndCurrency } from '../../../shared/useAmountAndCurrency'
import { TargetsModalAttachment } from './TargetsModalAttachment'
import './index.less'
import { TargetsModalAsterisk, TargetsModalMessage } from './TargetsModalMessageComponents'
import RichTextBox from '../RichTextBox'

export const TargetsModalFormResignedEmployeeTarget: FC = () => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { formatCurrency } = useAmountAndCurrency(user.locale)
  const { utcDateStringToMoment, formatDateShort } = useDateTime(user)

  const [currentObjective, currentComments, modalProps] = useTargetsModalStore(
    (state) => [state.currentObjective, state.currentComments, state.modalProps],
    shallow,
  )

  const formatDate = (date: string) => {
    return formatDateShort(date, true) || '-'
  }

  return !!currentObjective ? (
    <>
      <PerFlexRow direction="column">
        <PerText appearance="label-small" mb="xxs">
          {t('targetModal.title')}
        </PerText>
        <PerText fontWeight="bold">{currentObjective.title}</PerText>
      </PerFlexRow>

      <PerFlexRow mt="xl">
        <PerText appearance="tile-heading">{t('targetModal.tasks')}</PerText>
      </PerFlexRow>

      <PerFlexRow direction="column">
        <PerSpacer mb="xs">
          <PerProgressBar
            tasksDone={currentObjective.keyResults.filter(({ completed }) => completed).length}
            tasksAll={currentObjective.keyResults.length}
          />
        </PerSpacer>
        {currentObjective.keyResults.map((target, index) => {
          return (
            <PerFlexRow mb="s" key={index} className="fullWidth" align="middle" justify="start" noWrap>
              <PerFlexRow className="per-formikKeyResultsItem">
                <Checkbox checked={target.completed} disabled>
                  {target.text}
                </Checkbox>
              </PerFlexRow>
            </PerFlexRow>
          )
        })}
      </PerFlexRow>

      {currentObjective.taskNotes && (
        <>
          <PerFlexRow mt="xl">
            <PerText appearance="tile-heading">{t('targetModal.notes')}</PerText>
          </PerFlexRow>
          <PerFlexRow direction="column" mt="m" mb="xxl">
            <RichTextBox text={currentObjective.taskNotes} readonly />
          </PerFlexRow>
        </>
      )}

      <PerFlexRow mt="l">
        <Col span={8}>
          <PerText appearance="label-small" mb="xxs">
            {t('targetModal.startDate')}
          </PerText>
          <PerText>{formatDate(currentObjective.start)}</PerText>
        </Col>
        <Col span={1} />
        <Col span={8}>
          <PerText appearance="label-small" mb="xxs">
            {t('targetModal.dueDate')}
          </PerText>
          <PerText>{formatDate(currentObjective.due)}</PerText>
        </Col>
      </PerFlexRow>

      {!!currentObjective.targetAmount && (
        <>
          <PerFlexRow mt="m">
            {!!currentObjective.achievedAmount && (
              <>
                <Col span={8}>
                  <PerText appearance="label-small" mb="xxs">
                    {t('targetModal.achievedAmount')}
                  </PerText>
                  {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
                  <PerText fontWeight="bold">{formatCurrency(currentObjective.achievedAmount, currentObjective.currency!, true)}</PerText>
                  <PerText mt="xxxxs">{(currentObjective.achievedAmount / currentObjective.targetAmount) * 100 + '%'}</PerText>
                </Col>
                <Col span={1} />
              </>
            )}
            <Col span={8}>
              <PerText appearance="label-small" mb="xxs">
                {t('targetModal.targetAmount')}
                {modalProps.employeeCountry && modalProps.employeeCountry === Country.Pol && <TargetsModalAsterisk />}
              </PerText>
              {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
              <PerText fontWeight="bold">{formatCurrency(currentObjective.targetAmount, currentObjective.currency!, true)}</PerText>
            </Col>
          </PerFlexRow>
          {modalProps.employeeCountry && modalProps.employeeCountry === Country.Pol && (
            <TargetsModalMessage message={t(`targetModal.targetMessage.${Country.Pol}`)} />
          )}
        </>
      )}

      <PerSpacer mt="m" />

      <TargetsModalAttachment showDropZone={false} />

      <Divider />

      <PerFlexRow>
        <PerText appearance="tile-heading">{t('targetModal.comments')}</PerText>
      </PerFlexRow>

      {currentComments
        .slice()
        .sort((a, b) => {
          const date1 = utcDateStringToMoment(a.date)
          const date2 = utcDateStringToMoment(b.date)
          return date1 && date2 && date1.isBefore(date2) ? -1 : date1 && date2 && date1.isAfter(date2) ? 1 : 0
        })
        .map((note, index) => {
          return <TargetsModalComment comment={note} index={index} key={`${index}${note.date}`} />
        })}
    </>
  ) : null
}
